import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Switch,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import PhoneNumberField from '../../../shared/components/Forms/PhoneNumberField.component';
import { ParsedAddress, buildAddressLabel } from '../../../shared/components/Forms/parse-google-address.util';
import { DeliveryPointType } from '../../types/delivery-point.type';
import { DeliveryPointDetailsAddress } from '../../types/delivery-point-details.type';
import { CreateDeliveryPointRequestDTO } from '../../types/dtos/create-delivery-point.dto';
import { DELIVERY_POINT_TYPES, DELIVERY_POINT_TYPE_LABELS } from '../../constants/delivery-point-type.constants';
import {
  CreateDeliveryPointFormValues,
  DISCOUNT_ERROR,
  EMAIL_ERROR,
  EMPTY_CREATE_DELIVERY_POINT_FORM_VALUES,
  NAME_ERROR,
  buildCreateDeliveryPointPayload,
  getCreateDeliveryPointFormErrors,
  isCreateDeliveryPointFormValid,
} from '../../utils/create-delivery-point-form.util';
import { CODE_ERROR, isCompanyType } from '../../utils/delivery-point-form-rules.util';
import { DeliveryPointAddressFields } from '../Forms/DeliveryPointAddressFields.component';
import { DeliveryPointSegmentAutocomplete } from '../Forms/DeliveryPointSegmentAutocomplete.component';

const ID_PREFIX = 'create-delivery-point';

type FlatField = 'name' | 'phone' | 'email' | 'website' | 'code' | 'discount';

type Props = {
  open: boolean;
  isSubmitting: boolean;
  onSubmit: (data: CreateDeliveryPointRequestDTO) => Promise<void> | void;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  switchBase: {
    '&$switchChecked': {
      color: theme.palette.admin.primary.main,
    },
    '&$switchChecked + $switchTrack': {
      backgroundColor: theme.palette.admin.primary.main,
    },
  },
  switchChecked: {},
  switchTrack: {},
  createButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function CreateDeliveryPointDialog({ open, isSubmitting, onSubmit, onClose }: Props) {
  const classes = useStyles();

  const [values, setValues] = useState<CreateDeliveryPointFormValues>(EMPTY_CREATE_DELIVERY_POINT_FORM_VALUES);
  const [addressLabel, setAddressLabel] = useState('');
  const [isPlaceSelected, setIsPlaceSelected] = useState(false);
  const lastSelectedAddressLabelRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;

    lastSelectedAddressLabelRef.current = null;
    setValues(EMPTY_CREATE_DELIVERY_POINT_FORM_VALUES);
    setAddressLabel('');
    setIsPlaceSelected(false);
  }, [open]);

  const updateAddress = useCallback(
    (addressEdits: Partial<DeliveryPointDetailsAddress>) =>
      setValues((previous) => ({ ...previous, address: { ...previous.address, ...addressEdits } })),
    []
  );

  const handleAddressSelected = useCallback(
    (place: ParsedAddress) => {
      lastSelectedAddressLabelRef.current = buildAddressLabel(place);
      setIsPlaceSelected(true);
      updateAddress({ street: place.street, zipCode: place.zipCode, city: place.city, country: place.country });
    },
    [updateAddress]
  );

  const handleAddressLabelChange = useCallback(
    (value: string) => {
      setAddressLabel(value);

      if (value === lastSelectedAddressLabelRef.current) return;

      lastSelectedAddressLabelRef.current = null;
      setIsPlaceSelected(false);
      updateAddress({ street: '', zipCode: '', city: '', country: '' });
    },
    [updateAddress]
  );

  const handleFieldChange = (field: FlatField, value: string) =>
    setValues((previous) => ({ ...previous, [field]: value }));

  const errors = getCreateDeliveryPointFormErrors(values);
  const isCompany = isCompanyType(values.type);
  const canSubmit = isPlaceSelected && isCreateDeliveryPointFormValid(values) && !isSubmitting;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit || values.type === '' || !values.segment) return;

    onSubmit(buildCreateDeliveryPointPayload(values, values.type, values.segment.id));
  };

  return (
    <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={isSubmitting} fullWidth maxWidth="sm">
      <DialogTitle>Créer un point de livraison</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            id={`${ID_PREFIX}-name`}
            label="Nom"
            error={values.name !== '' && errors.isNameInvalid}
            helperText={values.name !== '' && errors.isNameInvalid ? NAME_ERROR : undefined}
            value={values.name}
            onChange={(event) => handleFieldChange('name', event.target.value)}
          />
          <div className={classes.row}>
            <FormControl variant="outlined" size="small" required>
              <InputLabel htmlFor={`${ID_PREFIX}-type`}>Type</InputLabel>
              <Select
                native
                label="Type"
                value={values.type}
                inputProps={{ id: `${ID_PREFIX}-type` }}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setValues((previous) => ({ ...previous, type: event.target.value as DeliveryPointType | '' }))
                }
              >
                <option value="" aria-label="Aucun" />
                {DELIVERY_POINT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {DELIVERY_POINT_TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
            </FormControl>
            <DeliveryPointSegmentAutocomplete
              id={`${ID_PREFIX}-segment`}
              required
              value={values.segment}
              onChange={(segment) => setValues((previous) => ({ ...previous, segment }))}
            />
          </div>
          <DeliveryPointAddressFields
            idPrefix={ID_PREFIX}
            required
            address={values.address}
            addressLabel={addressLabel}
            isPlaceSelected={isPlaceSelected}
            onAddressLabelChange={handleAddressLabelChange}
            onAddressSelected={handleAddressSelected}
            onAddressFieldChange={({ field, value }) => updateAddress({ [field]: value })}
          />
          <div className={classes.row}>
            <PhoneNumberField
              id={`${ID_PREFIX}-phone`}
              value={values.phone}
              onChange={(digits) => handleFieldChange('phone', digits)}
            />
            <TextField
              size="small"
              variant="outlined"
              id={`${ID_PREFIX}-email`}
              label="Email"
              type="email"
              error={errors.isEmailInvalid}
              helperText={errors.isEmailInvalid ? EMAIL_ERROR : undefined}
              value={values.email}
              onChange={(event) => handleFieldChange('email', event.target.value)}
            />
          </div>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            id={`${ID_PREFIX}-website`}
            label="Site web"
            value={values.website}
            onChange={(event) => handleFieldChange('website', event.target.value)}
          />
          {isCompany && (
            <div className={classes.row}>
              <TextField
                size="small"
                variant="outlined"
                required
                id={`${ID_PREFIX}-code`}
                label="Code entreprise"
                inputProps={{ maxLength: 20 }}
                error={values.code !== '' && errors.isCodeInvalid}
                helperText={values.code !== '' && errors.isCodeInvalid ? CODE_ERROR : undefined}
                value={values.code}
                onChange={(event) => handleFieldChange('code', event.target.value)}
              />
              <TextField
                size="small"
                variant="outlined"
                id={`${ID_PREFIX}-discount`}
                label="Remise (%)"
                type="number"
                inputProps={{ min: 0, max: 100 }}
                error={errors.isDiscountInvalid}
                helperText={errors.isDiscountInvalid ? DISCOUNT_ERROR : undefined}
                value={values.discount}
                onChange={(event) => handleFieldChange('discount', event.target.value)}
              />
            </div>
          )}
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="default"
                classes={{ switchBase: classes.switchBase, checked: classes.switchChecked, track: classes.switchTrack }}
                checked={values.isActive}
                onChange={(event) => setValues((previous) => ({ ...previous, isActive: event.target.checked }))}
              />
            }
            label="Actif"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
